import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Input } from "@shadcn-in-nx/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shadcn-in-nx/ui/table";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Loader2, Search, Lock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export default function SettingsTab({ companyId }) {
    const { t } = useTranslation();
    const [settings, setSettings] = useState([]);
    const [search, setSearch] = useState("");
    const { call: fetchSettings, isLoading } = useBluelagoon({ method: "getCompanySettings" });

    const load = useCallback(() => {
        fetchSettings({ companyId })
            .then((res) => setSettings(res?.data?.settings || []))
            .catch(() => setSettings([]));
    }, [fetchSettings, companyId]);

    useEffect(() => { load(); }, [load]);

    const q = search.trim().toLowerCase();
    const filtered = q
        ? settings.filter((s) =>
            s.key.toLowerCase().includes(q) ||
            (s.value || "").toLowerCase().includes(q))
        : settings;

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder={t("admin.manageCompany.settings.search", "Search settings...")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9 h-8"
                    />
                </div>
                <span className="text-xs text-muted-foreground">{t("admin.manageCompany.settings.total", "{{count}} settings", { count: settings.length })}</span>
            </div>
            {isLoading && settings.length === 0 ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-1/3">{t("admin.manageCompany.settings.key", "Key")}</TableHead>
                            <TableHead>{t("admin.manageCompany.settings.value", "Value")}</TableHead>
                            <TableHead className="w-40">{t("admin.manageCompany.settings.updated", "Updated")}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filtered.length === 0 ? (
                            <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">
                                {q ? t("admin.manageCompany.settings.noMatch", "No settings match your search") : t("admin.manageCompany.settings.empty", "No settings")}
                            </TableCell></TableRow>
                        ) : filtered.map((s) => (
                            <TableRow key={s.id}>
                                <TableCell className="font-mono text-xs align-top">
                                    <div className="flex items-center gap-1">
                                        {s.key}
                                        {s.sensitive && <Lock className="h-3 w-3 text-muted-foreground" />}
                                    </div>
                                </TableCell>
                                <TableCell className="text-xs align-top">
                                    {s.sensitive ? (
                                        <span className="inline-flex items-center gap-1">
                                            <Badge variant="outline" className="text-[10px]">{t("admin.manageCompany.settings.masked", "masked")}</Badge>
                                            <code className="font-mono">{s.value}</code>
                                        </span>
                                    ) : (
                                        <code className="font-mono break-all whitespace-pre-wrap">{s.value || ""}</code>
                                    )}
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground align-top">
                                    {s.updatedAt ? formatDistanceToNow(new Date(s.updatedAt), { addSuffix: true }) : "—"}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </div>
    );
}
